
<template>
  <v-container fluid>
    <div class="d-flex justify-space-between">
      <div cols="2" sm="2" class="d-flex text-center text-sm-left">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="my-auto" v-bind="attrs" v-on="on">
              <v-btn icon>
                <v-icon>mdi-calendar</v-icon>
              </v-btn>

              <span v-if="dateRangeMode == 'range'">
                {{ formattedDateRange }}
              </span>
              <span v-else> {{ dateRange.start | formatDate }} </span>
            </div>
          </template>

          <v-card>
            <v-card-text>
              <date-range-picker
                :mode="dateRangeMode"
                v-model="dateRange"
                @on-next="updateChart"
                @on-prev="updateChart"
                @on-predefine="updateChart"
              />
            </v-card-text>
          </v-card>
        </v-menu>
      </div>

      <div cols="2" sm="2" class="text-center text-sm-right">
        <schedule-support-menu
          :dateRange="dateRange"
          :teachers="teachers"
          :classrooms="classrooms"
          :items="items"
        />
      </div>
    </div>

    <v-divider class="mb-4" />

    <div
      v-if="rows && rows.length > 0"
      class="menu d-flex justify-space-between"
    >
      <div class="mr-4 mr-md-0 my-auto">
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-badge
              v-bind="attrs"
              :value="filterStaff"
              small
              :content="1"
              overlap
              color="red"
            >
              <v-icon large color="black" v-on="on">
                mdi-filter-outline
              </v-icon>
            </v-badge>
          </template>

          <v-card>
            <v-card-title> Filter Staff </v-card-title>
            <v-card-text>
              <v-autocomplete
                label="Filter Staff"
                outlined
                dense
                prepend-inner-icon="mdi-magnify"
                v-model="filterStaff"
                :items="teachers"
                item-text="firstName"
                clearable
                return-object
              />
            </v-card-text>
            <v-card-actions v-if="filterStaff" class="justify-center pb-4">
              <v-btn @click="filterStaff = null" text> Clear Filter </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </div>

      <div v-if="!isMobile" class="flex-grow-1 mx-0 mx-md-4">
        <global-lookup />
      </div>

      <div class="d-flex my-auto">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mr-2" icon v-bind="attrs" v-on="on">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="text-subtitle-1">Zoom</v-card-title>
            <v-card-text>
              <v-slider
                class="mx-6"
                vertical
                v-model="zoom"
                :min="8"
                :max="16"
                append-icon="mdi-magnify-plus-outline"
                prepend-icon="mdi-magnify-minus-outline"
                thumb-label
                @click:append="zoomIn"
                @click:prepend="zoomOut"
              />
            </v-card-text>
          </v-card>
        </v-menu>

        <v-btn-toggle v-model="displayGroups" dense multiple>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn value="teacher" v-bind="attrs" v-on="on">
                <v-icon>mdi-account</v-icon>
              </v-btn>
            </template>
            <span>{{ terminologies.teacher }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn value="classroom" v-bind="attrs" v-on="on">
                <v-icon>mdi-google-classroom</v-icon>
              </v-btn>
            </template>
            <span>Classroom</span>
          </v-tooltip>
        </v-btn-toggle>

        <v-btn @click="openAttendanceDialog" class="mx-2 mb-1" icon>
          <v-icon large>mdi-checkbox-multiple-marked-circle-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="isMobile" class="flex-grow-1 mx-0 mx-md-4">
      <global-lookup />
    </div>

    <div class="mt-2">
      <div id="chart-section" :class="{ fullscreen: fullscreen }">
        <v-sheet :elevation="3" style="position: relative">
          <div v-if="prevDateRange && !dialog">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  fab
                  absolute
                  top
                  left
                  @click="back2PrevDateRange"
                  v-on="on"
                  v-bind="attrs"
                >
                  <v-icon color="primary">
                    mdi-arrow-left-circle-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>
                Back to {{ prevDateRange.start | formatDate }} -
                {{ prevDateRange.end | formatDate }}
              </span>
            </v-tooltip>
          </div>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                v-show="prevDateRange == null && dateRangeMode == 'range'"
                small
                fab
                absolute
                top
                left
                @click="previousWeek"
                :style="`${fullscreen ? 'top: 5px':''}`"
              >
                <v-icon color="primary"> mdi-arrow-left-circle-outline </v-icon>
              </v-btn>
            </template>
            <span>Previous Week</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                v-show="prevDateRange == null && dateRangeMode == 'range'"
                small
                fab
                absolute
                top
                right
                @click="nextWeek"
                :style="`${fullscreen ? 'top: 5px':''}`"
              >
                <v-icon color="primary">
                  mdi-arrow-right-circle-outline
                </v-icon>
              </v-btn>
            </template>
            <span>Next Week</span>
          </v-tooltip>

          <v-speed-dial
  v-model="speedDialStatus"
  direction="top"
  absolute
  bottom
  right
  style="bottom: 8px;"
>
  <template v-slot:activator>
    <v-btn small fab color="primary">
      <v-icon v-if="speedDialStatus"> mdi-close </v-icon>
      <v-icon v-else> mdi-dots-vertical </v-icon>
    </v-btn>
  </template>

  <div class="button-container">
    <span class="caption-text">Fullscreen</span> 
    <v-btn small fab @click="toggleFullscreen">
      <v-icon color="primary">
        {{ fullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}
      </v-icon>
    </v-btn>
  </div>

  <div class="button-container">
    <span class="caption-text">Reload</span> 
    <v-btn small fab @click="updateChart">
      <v-icon color="primary"> mdi-refresh </v-icon>
    </v-btn>
  </div>
</v-speed-dial>



          <v-speed-dial
            v-model="showLegend"
            :direction="$vuetify.breakpoint.smAndDown ? 'top' : 'right'"
            absolute
            bottom
            left
            style="bottom: 8px;"
          >
            <template v-slot:activator>
              <v-btn small fab color="primary">
                <v-icon v-if="showLegend"> mdi-close </v-icon>
                <v-icon v-else> mdi-information-variant </v-icon>
              </v-btn>
            </template>

            <schedule-chart-legend
              class="ml-12 ml-md-0"
              v-model="filterRowTypes"
              @stateChange="handleLegendStateChange"
            />
          </v-speed-dial>

          <vue-dj-gantt
            :from="dateRange.start"
            :to="calculatedRangeEndDate"
            :list="list"
            :rows="filteredRows"
            :items="filteredItems"
            :zoomLevel="zoom"
            locale="en"
            :height="fullscreen ? viewportHeight - 15 : viewportHeight - 250"
            :externalScrollLeft="chartScrollLeft"
            @zoom="zoomChanged"
            @row="selectedRow"
            @cell="selectedCell"
            @dblclick-cell="onCellDoubleClicked"
            @dblclick-item="onItemDoubleClicked"
            @time-slot="onTimeslotClicked"
            @resized="updateItemOnResize"
            @moved="updateItemOnMove"
            @scroll-x="scrollXChanged"
          >
            <template v-slot:row-label="item">
              <div class="d-flex justify-space-between">
                <v-icon class="my-auto mr-2 bg-icon">
                  {{
                    item.row.groupType == "classroom"
                      ? "mdi-google-classroom"
                      : "mdi-account"
                  }}
                </v-icon>
                <div class="flex-grow-1 text-ellipsis">{{ item.row.name }}</div>
                <span v-if="!isAtMinWidth" class="mx-2">
                  <row-support-menu
                    :rowItem="item"
                    :dateRange="dateRange"
                    :teachers="teachers"
                    :classrooms="classrooms"
                  />
                </span>
              </div>
            </template>
          </vue-dj-gantt>
        </v-sheet>
      </div>

      <v-overlay :value="overlay" absolute z-index="101">
        <v-progress-circular v-if="loading" indeterminate></v-progress-circular>
        <div v-if="!loading">
          Date range changed, please reload data...

          <div class="text-center">
            <v-btn class="ma-2" text @click="updateChart" :loading="loading">
              <v-icon left dark> mdi-refresh </v-icon> Reload
            </v-btn>
          </div>
        </div>
      </v-overlay>
    </div>

    <div class="text-center">
      <p v-if="rows && rows.length > 0" class="text-subtitle-1">
        No data found for the selected date range ({{
          dateRange.start | formatDate
        }}
        - {{ dateRange.end | formatDate }}). Please choose another date range or
        enter new schedules.
      </p>

      <v-overlay :value="overlay" absolute z-index="101">
        <v-progress-circular v-if="loading" indeterminate></v-progress-circular>
        <div v-if="!loading">
          Date range changed, please reload data...

          <div class="text-center">
            <v-btn class="ma-2" text @click="updateChart" :loading="loading">
              <v-icon left dark> mdi-refresh </v-icon> Reload
            </v-btn>
          </div>
        </div>
      </v-overlay>
    </div>

    <v-dialog
      v-for="(dialog, index) in dialogs"
      :key="index"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="dialog.show"
      max-width="600px"
      :style="{ zIndex: computedZIndex(index) }"
    >
      <v-card>
        <v-card-title class="justify-space-between drag-handle">
          <span class="text-subtitle-2 overflow-title">
            {{ dialog.title }}
          </span>
          <v-btn class="my-auto" icon @click="closeDialog(index)">
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="mb-2" />
        <v-card-text>
          <v-progress-circular
            v-if="dialog.loading"
            indeterminate
            class="mx-auto my-5"
          />

          <pre v-if="dialog.mode == 'debug'"> {{ dialog.data }} </pre>
          <component
            v-else
            :is="dialog.mode"
            v-bind="dialog.data"
            @on-close="closeDialog(index)"
            @loading-started="startLoading(index)"
            @loading-finished="finishLoading(index)"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>


<script>
import { EventBus } from "@/EventBus.js";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { fetchStaffAndStudentSchedules } from "@/services/schedulingService.js";
import {
  generateGanttItems,
  generateGanttRows,
  updateRowTotalCountByType,
} from "@/utils/scheduling/schedulingGanttItemsGenerator.js";
import { ResizeMove } from "@/utils/scheduling/resizeMove.js";
import { handleItem, handleCell, openAttendance } from "./eventHandler.js";

import * as DynamicComponents from "./componentRegistry.js";

export default {
  components: {
    ...DynamicComponents,
  },
  created() {
    EventBus.$on("reload-chart", () => {
      this.updateChart();
    });

    EventBus.$on("open-dialog", (title, mode, data, parentID) => {
      if (this.dialogs.length < this.maxDialogs) {
        const dialogUUID = this.generateUUID();

        //append global variables to data:
        data.teachers = this.teachers;
        data.classrooms = this.classrooms;
        data.items = this.items;
        data.rows = this.rows;
        data.dateRange = this.dateRange;
        data.uuID = dialogUUID;
        (data.parentID = parentID ? parentID : "1"),
          this.dialogs.push({
            title: title,
            mode: mode,
            data: data,
            loading: false,
            show: true,
            id: dialogUUID,
          });
      } else {
        this.addErrorMessage("Max number of dialogs reached!");
      }
    });
  },
  beforeMount() {
    this.hideDrawer();
  },
  mounted() {
    this.updateViewportHeight(); // Initially set to viewport height
    window.addEventListener("resize", this.updateViewportHeight);

    this.initChartFromData();
  },
  beforeDestroy() {
    // to avoid memory leaks
    EventBus.$off("open-dialog");
    EventBus.$off("reload-chart");

    window.removeEventListener("resize", this.updateViewportHeight);
  },
  computed: {
    dateRange: {
      get() {
        return this.$store.state.scheduleCentral.dateRange;
      },
      set(value) {
        this.setDateRange(value);
      },
    },
    formattedDateRange() {
      const start = new Date(this.dateRange.start);
      const end = new Date(this.dateRange.end);

      if (start.toDateString() === end.toDateString()) {
        return moment(start).format("ddd, MMM DD YYYY");
      } else if (start.getFullYear() === end.getFullYear()) {
        let startDateFormat = moment(start).format("ddd, MMM DD");
        let endDateFormat = moment(end).format("ddd, MMM DD YYYY");
        return `${startDateFormat} - ${endDateFormat}`;
      } else {
        let startDateFormat = moment(start).format("ddd, MMM DD YYYY");
        let endDateFormat = moment(end).format("ddd, MMM DD YYYY");
        return `${startDateFormat} - ${endDateFormat}`;
      }
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    calculatedRangeEndDate() {
      return moment(this.dateRange.end).add(1, "days").format("YYYY-MM-DD");
    },
    firstItemHour() {
      if (this.zoom > 12) {
        const rowIds = this.filteredRows.map((r) => r.id);

        // Filter out the items where the rowId is not in the rowIds array
        const filteredItems = this.filteredItems.filter((item) =>
          rowIds.includes(item.rowId)
        );

        // Find the item with the earliest startTime
        const earliestItem = filteredItems.reduce((prevItem, currItem) => {
          const prevStartTime = moment(prevItem.time.start).format("HH:mm");
          const currStartTime = moment(currItem.time.start).format("HH:mm");
          return currStartTime <= prevStartTime ? currItem : prevItem;
        }, filteredItems[0]); // initialized with the first item

        // Extract the hour from the earliestItem's startTime
        const earliestHour = earliestItem
          ? moment(earliestItem.time.start).hour()
          : 0;

        return earliestHour;
      } else {
        return 0;
      }
    },
    filteredItems() {
      if (this.filterRowTypes.includes("teacher-availability")) {
        return this.items;
      } else {
        if (this.items) {
          return this.items.filter((item) => item.type != "availability");
        } else {
          return [];
        }
      }
    },
    filteredRows() {
      const filteredRows = this.rows.filter((row) => {
        return (
          (!this.filterStaff ||
            row.name
              .toLowerCase()
              .includes(this.filterStaff.firstName.toLowerCase())) &&
          this.filterRowTypes.includes(row.type) && // Use the stored reference here
          this.displayGroups.includes(row.groupType) // New filter criterion
        );
      });

      const nonEmptyRows = filteredRows.filter((row) => {
        return row.totalCount > 0;
      });

      this.updateTeacherCount(nonEmptyRows);

      return nonEmptyRows;
    },

    ...mapGetters(["globalCenter"]),
    ...mapGetters("terminology", ["terminologies"]),
  },
  watch: {
    zoom: {
      immediate: false,
      handler(newZoom, oldZoom) {
        const oldGroupBy = oldZoom > 12 ? "hour" : "day";
        const newGroupBy = newZoom > 12 ? "hour" : "day";

        // Only rerun if groupBy has changed
        if (oldGroupBy !== newGroupBy) {
          this.initChartFromData();
        }
      },
    },
    dateRange: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.overlay = true;
          // Reset chart-related data here
          // this.rawData = [];
          // this.rows = [];
          // this.items = [];
          // this.teachers = [];
          // this.students = [];
        }
      },
      deep: true, // ensure watcher fires even when nested properties of dateRange object change
    },
  },
  data: () => ({
    rawData: [],
    prevDateRange: null,
    dateRangeMode: "range",
    viewportHeight: 0,
    fullscreen: false,
    isAtMinWidth: false,
    list: [
      {
        id: "name",
        width: 200,
        lastXVal: 0,
        header: {
          content: "0 Teachers",
        },
      },
    ],
    rows: [],
    items: [],
    teachers: [],
    students: [],
    classrooms: [],
    filterStaff: "",
    filterRowTypes: [],
    displayGroups: ["classroom", "teacher"],
    speedDialStatus: false,
    showLegend: false,
    dialogs: [],
    maxDialogs: 5,
    baseDialogZIndex: 1000,
    loading: false,
    overlay: false,
    zoom: 11,
    chartScrollLeft: 0,
    chartWidth: 0,
    cellWidth: 0,
  }),
  methods: {
    ...mapActions([
      "addErrorMessage",
      "addSuccessMessage",
      "clearMessage",
      "hideDrawer",
    ]),
    ...mapActions("scheduleCentral", ["setDateRange"]),
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    updateViewportHeight() {
      const minHeight = 600;
      this.viewportHeight =
        window.innerHeight < minHeight ? minHeight : window.innerHeight;
    },
    back2PrevDateRange() {
      if (this.prevDateRange) {
        this.dateRange = { ...this.prevDateRange };
        this.dateRangeMode = "range";
        this.prevDateRange = null;
        this.updateChart();
      }
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen;
    },
    startLoading(index) {
      this.dialog[index].loading = true;
    },
    finishLoading(index) {
      this.dialog[index].loading = false;
    },
    computedZIndex(index) {
      return this.baseDialogZIndex + index;
    },
    previousWeek() {
      let startOfWeek = moment(this.dateRange.start).startOf("isoWeek");
      let prevWeekStart = startOfWeek.subtract(1, "weeks").format("YYYY-MM-DD");
      let prevWeekEnd = moment(prevWeekStart)
        .endOf("isoWeek")
        .format("YYYY-MM-DD");
      this.dateRange = { start: prevWeekStart, end: prevWeekEnd };
      this.updateChart();
    },

    nextWeek() {
      let startOfWeek = moment(this.dateRange.start).startOf("isoWeek");
      let nextWeekStart = startOfWeek.add(1, "weeks").format("YYYY-MM-DD");
      let nextWeekEnd = moment(nextWeekStart)
        .endOf("isoWeek")
        .format("YYYY-MM-DD");
      this.dateRange = { start: nextWeekStart, end: nextWeekEnd };
      this.updateChart();
    },
    zoomChanged({ zoom, width, cells }) {
      this.chartWidth = width;
      this.cellWidth = cells && cells.length > 0 ? cells[0].width : 0;
      // hour view chart
      if (zoom > 12) {
        this.initScrollLeftPosition(this.cellWidth);
      }
    },
    scrollXChanged(newXVal) {
      // Check if not on mobile device
      if (!this.$vuetify.breakpoint.mobile) {
        return;
      }

      const minWidth = 100;
      const maxWidth = 200;
      const tolerance = 2; // Adjust as needed

      if (newXVal >= maxWidth) {
        return;
      }

      // Calculate the width difference based on the direction of scrolling
      let widthDiff = this.list[0].lastXVal - newXVal;

      // Calculate the new width
      let newWidth = this.list[0].width + widthDiff;

      // Make sure the new width is within the bounds of 100 and 200
      newWidth = Math.max(minWidth, newWidth); // If newWidth is less than 100, set it to 100
      newWidth = Math.min(maxWidth, newWidth); // If newWidth is more than 200, set it to 200

      // Update the width of the first item in the list
      this.list[0].width = newWidth;

      // Set isAtMinWidth flag
      this.isAtMinWidth = Math.abs(newWidth - minWidth) <= tolerance;

      // Update the last X value
      this.list[0].lastXVal = newXVal;
    },
    initScrollLeftPosition(cellWidth) {
      const newScrollPosition = cellWidth * this.firstItemHour - 1;

      //Need to update to force reactivity in the gantt chart
      this.chartScrollLeft =
        newScrollPosition == this.chartScrollLeft
          ? this.chartScrollLeft + 1
          : newScrollPosition;

      console.log("set new scroll left: " + this.chartScrollLeft);
    },
    async fetchInitialData() {
      const selectedCenter = this.globalCenter ? this.globalCenter.id : null;
      const { groupedData, teachers, students, classrooms } =
        await fetchStaffAndStudentSchedules(
          selectedCenter,
          this.dateRange.start,
          this.dateRange.end
        );
      this.rawData = groupedData;

      this.teachers = teachers.filter((t) => t.id);
      this.students = students;
      this.classrooms = classrooms.filter((c) => c.id);
    },
    getStaffCount(rows) {
      if (rows) {
        const teacherIds = new Set(
          rows
            .filter(
              (r) =>
                (r.type == "student-schedule" || r.type == "schedule") &&
                r.entityId
            )
            .map((r) => r.entityId)
        );
        return teacherIds.size;
      } else {
        return 0;
      }
    },
    generateItems(rows) {
      const groupBy = this.zoom > 12 ? "hour" : "day";

      this.items = generateGanttItems(this.rawData, rows, groupBy);
    },
    async initChartFromData() {
      this.loading = true;
      this.list[0].lastXVal = 0;

      await this.fetchInitialData();

      this.rows = generateGanttRows(this.rawData);
      this.generateItems(this.rows);

      // initialize displayed rows (teacher/students)
      this.filterRowTypes = Array.from(
        new Set(this.items.map((item) => item.type))
      );

      if (this.items && this.rows) {
        updateRowTotalCountByType(this.items, this.rows);
      }

      if (this.cellWidth) {
        this.initScrollLeftPosition(this.cellWidth);
      }
      this.loading = false;
      this.overlay = false;
    },

    async updateChart() {
      if (this.dateRange.start > this.dateRange.end) {
        this.addErrorMessage("Start date should be before end date!");
        return;
      }

      this.overlay = true;
      await this.initChartFromData();

      // Calculate the difference in days between the start and end dates
      const startDate = moment(this.dateRange.start);
      const endDate = moment(this.dateRange.end);
      const diffDays = endDate.diff(startDate, "days");

      if (this.dateRange.start == this.dateRange.end) {
        this.zoom = 16;
      } else if (diffDays >= 2 && diffDays < 4) {
        this.zoom = 12;
      } else if (diffDays >= 4 && diffDays <= 7) {
        this.zoom = 11;
      } else if (diffDays > 7) {
        this.zoom = 10;
      } else {
        this.zoom = 12;
      }
    },
    closeDialog(index) {
      this.dialogs.splice(index, 1);
    },
    onTimeslotClicked(slot) {
      // zoom into day if zoom is at date level
      if (this.zoom <= 12) {
        // Check if there are any items start time within the slot range (date)
        const slotDate = moment(slot.moment).format("YYYY-MM-DD");
        const itemInSlot = this.items.find((item) =>
          moment(item.time.start).isSame(slotDate, "day")
        );

        // If there is no item in the slot, return early
        if (!itemInSlot) {
          return;
        }

        this.prevDateRange = { ...this.dateRange };
        this.dateRange = { start: slotDate, end: slotDate };
        this.dateRangeMode = "single";

        this.updateChart();
      }
    },
    onCellDoubleClicked(event) {
      const row = event.row.row;
      const selectedDate = event.slot.moment;

      handleCell(row, selectedDate);
    },
    onItemDoubleClicked(event) {
      const row = event.row.row;
      const item = event.item;
      const actionEvent = item.type;

      handleItem(actionEvent, item, row);
    },
    openAttendanceDialog() {
      openAttendance();
    },
    selectedRow(event) {
      console.log(event);
    },
    zoomOut() {
      this.zoom = this.zoom - 1 || 8;
    },
    zoomIn() {
      this.zoom = this.zoom + 1 || 16;
    },
    updateItemOnResize(event) {
      const updatedItem = ResizeMove.resizedItem(event);
      event.item = updatedItem;
    },
    updateItemOnMove(event) {
      const updatedItem = ResizeMove.movedItem(event);
      event.item = updatedItem;
    },
    handleLegendStateChange(legendItems) {
      this.filterRowTypes = legendItems
        .filter((i) => i.active && i.type)
        .map((i) => i.type);

      this.showLegend = true;
    },
    updateTeacherCount(rows) {
      const teacherCount = this.getStaffCount(rows);
      this.list[0].header.content = `${teacherCount} Teachers`;
    },
  },
};
</script>

<style scoped>
.bg-icon {
  opacity: 0.3;
  z-index: 1;
}
.text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100; /** v-dialog z-index is 200, allow dialog to show above it*/
}
#chart-section.fullscreen {
  margin: 0px;
}
.overflow-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dialog-transition-enter-active,
.dialog-transition-leave-active {
  transition: transform 0.5s;
}

.dialog-transition-enter,
.dialog-transition-leave-to {
  transform: translateY(-100%);
}

.drag-handle {
  background-color: #e8e8e8; /* slight gray to differentiate it from the content */
}

.button-container {
  position: relative;
}

.caption-text {
  position: absolute;
  left: -60px; /* Adjust as needed */
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #888;
  background-color: rgba(255, 255, 255, 0.6); /* Transparent white background */
  padding: 2px 4px; /* Padding around the text */
  border-radius: 3px; /* Optional: Rounded corners */
}

</style>